import React from "react";

/**
 * Reusable modal for confirmation actions like duplicate and renew
 *
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {string} title - Modal title
 * @param {string} message - Modal message body
 * @param {string} confirmText - Text for confirm button
 * @param {string} actionType - Type of action ('duplicate', 'renew', etc.)
 * @param {function} onConfirm - Function to call when action is confirmed
 * @param {function} onCancel - Function to call when action is canceled
 * @param {object} data - Data related to the action (transaction, etc.)
 */
const ActionModal = ({
  isOpen,
  title,
  message,
  confirmText = "Confirm",
  actionType,
  onConfirm,
  onCancel,
  data,
}) => {
  if (!isOpen) return null;

  const styles = {
    backdrop: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 999,
    },
    modal: {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      backgroundColor: "white",
      padding: "24px",
      borderRadius: "8px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      zIndex: 1000,
      maxWidth: "500px",
      width: "90%",
    },
    title: {
      marginBottom: "16px",
      fontSize: "20px",
      fontWeight: "600",
      color: actionType === "renew" ? "#065f46" : "#5b21b6",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    message: {
      marginBottom: "20px",
      lineHeight: "1.6",
      color: "#4B5563",
    },
    infoBox: {
      backgroundColor: actionType === "renew" ? "#ecfdf5" : "#f5f3ff",
      padding: "12px",
      borderRadius: "8px",
      marginBottom: "20px",
      border: `1px solid ${actionType === "renew" ? "#d1fae5" : "#ede9fe"}`,
    },
    infoTitle: {
      fontSize: "14px",
      fontWeight: "600",
      marginBottom: "8px",
      color: actionType === "renew" ? "#065f46" : "#5b21b6",
    },
    infoItem: {
      fontSize: "13px",
      lineHeight: "1.5",
      display: "flex",
      gap: "8px",
      marginBottom: "4px",
    },
    buttonContainer: {
      display: "flex",
      gap: "12px",
      justifyContent: "flex-end",
    },
    button: {
      flex: "1",
      maxWidth: "150px",
      padding: "0.75rem 1.5rem",
      color: "white",
      border: "none",
      borderRadius: "0.375rem",
      cursor: "pointer",
      transition: "background-color 0.2s",
      fontWeight: "500",
    },
    cancelButton: {
      backgroundColor: "#64748b",
      "&:hover": {
        backgroundColor: "#475569",
      },
    },
    confirmButton: {
      backgroundColor: actionType === "renew" ? "#10B981" : "#8B5CF6", // Green for renew, purple for duplicate
      "&:hover": {
        backgroundColor: actionType === "renew" ? "#059669" : "#7C3AED",
      },
    },
  };

  // Additional information for renewal action
  const renderAdditionalInfo = () => {
    if (actionType === "renew") {
      return (
        <div style={styles.infoBox}>
          <div style={styles.infoTitle}>What happens when you renew:</div>
          <div style={styles.infoItem}>
            <span>•</span>
            <span>A new transaction will be created with today's date</span>
          </div>
          <div style={styles.infoItem}>
            <span>•</span>
            <span>The original transaction will be marked as renewed</span>
          </div>
          <div style={styles.infoItem}>
            <span>•</span>
            <span>Both transactions will be linked for reference</span>
          </div>
          <div style={styles.infoItem}>
            <span>•</span>
            <span>The new transaction will have "Completed" status</span>
          </div>
        </div>
      );
    } else if (actionType === "duplicate") {
      return (
        <div style={styles.infoBox}>
          <div style={styles.infoTitle}>What happens when you duplicate:</div>
          <div style={styles.infoItem}>
            <span>•</span>
            <span>A new identical transaction will be created</span>
          </div>
          <div style={styles.infoItem}>
            <span>•</span>
            <span>The new transaction will have today's date</span>
          </div>
          <div style={styles.infoItem}>
            <span>•</span>
            <span>All other details will be copied exactly</span>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <div style={styles.backdrop} onClick={onCancel} />
      <div style={styles.modal}>
        <h3 style={styles.title}>{title}</h3>
        <p style={styles.message}>{message}</p>

        {renderAdditionalInfo()}

        <div style={styles.buttonContainer}>
          <button
            onClick={onCancel}
            style={{ ...styles.button, ...styles.cancelButton }}
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(data)}
            style={{ ...styles.button, ...styles.confirmButton }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </>
  );
};

export default ActionModal;
